import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Popconfirm,
  message,
  Space,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  useDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
} from "../../Services";
import { Department } from "../../Types";
import { modalFooter, modalTitle } from "../AppUi";

interface DepartmentModalProps {
  open: boolean;
  onClose: () => void;
}

export const DepartmentModal: React.FC<DepartmentModalProps> = ({
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [editingItem, setEditingItem] = useState<Department | null>(null);

  const { data: departments, isLoading } = useDepartmentsQuery({
    enabled: open,
  });

  const createMutation = useCreateDepartmentMutation();
  const updateMutation = useUpdateDepartmentMutation();
  const deleteMutation = useDeleteDepartmentMutation();

  const handleResetForm = () => {
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formattedValues = {
        name: values.name.trim(),
        description: values.description ? values.description.trim() : null,
      };

      if (editingItem) {
        updateMutation.mutate(
          { id: editingItem.id, ...formattedValues },
          {
            onSuccess: (res) => {
              if (res?.success) {
                message.success("Department updated successfully!");
                handleResetForm();
              } else {
                message.error(res?.message || "Failed to update department.");
              }
            },
            onError: () => message.error("Something went wrong."),
          },
        );
      } else {
        createMutation.mutate(formattedValues, {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Department added successfully!");
              handleResetForm();
            } else {
              message.error(res?.message || "Failed to add department.");
            }
          },
          onError: () => message.error("Something went wrong."),
        });
      }
    } catch {
      message.error("Please enter a department name.");
    }
  };

  const handleEditClick = (record: Department) => {
    setEditingItem(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description || "",
    });
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Department deleted successfully!");
          if (editingItem?.id === id) {
            handleResetForm();
          }
        } else {
          message.error(res?.message || "Failed to delete department.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 140,
      render: (text: string, record: Department) => (
        <Tag color="purple" className="font-mono text-xs">
          {text || record.name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_")}
        </Tag>
      ),
    },
    {
      title: "Department Name",
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <span className="font-semibold text-gray-800">{text}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) =>
        text ? (
          <span className="text-gray-600 text-xs">{text}</span>
        ) : (
          <span className="text-gray-300 text-xs">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center" as const,
      render: (_: any, record: Department) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditClick(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this department?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={modalTitle("Manage Departments")}
      open={open}
      onCancel={() => {
        handleResetForm();
        onClose();
      }}
      footer={modalFooter({
        actions: [
          {
            label: "Close",
            onClick: () => {
              handleResetForm();
              onClose();
            },
          },
        ],
      })}
      width={640}
    >
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
        <Form form={form} layout="vertical">
          <Form.Item
            label="Department Name"
            name="name"
            rules={[
              { required: true, message: "Department name is required" },
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="e.g. Sales, HR, IT, Production" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            style={{ marginBottom: 12 }}
          >
            <Input.TextArea
              rows={2}
              placeholder="Describe department function (optional)..."
              style={{ resize: "none" }}
            />
          </Form.Item>

          <div className="flex items-center justify-end gap-2">
            {editingItem && (
              <Button size="middle" onClick={handleResetForm}>
                Cancel Edit
              </Button>
            )}
            <Button
              type="primary"
              size="middle"
              onClick={handleSubmit}
              icon={editingItem ? <SaveOutlined /> : <PlusOutlined />}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editingItem ? "Update Department" : "Add Department"}
            </Button>
          </div>
        </Form>
      </div>

      <Table
        dataSource={departments || []}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </Modal>
  );
};
